import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { InboundListFilter } from '@/screens/order-inbounds/order-inbounds-order-list/types';
import {
  ORDER_INBOUNDS_STORAGE_KEYS,
  orderInboundsStorage,
} from '@/stores/utils/order-inbounds-storage';
import { SmartOrderInboundHistoryOrderBy } from '@/types';

export const DEFAULT_SPLIT_RATIO = 0.5;

export interface InboundListQueryFilters {
  /**
   * Employees whose inbounds to show. Unioned with `includeUnassigned`; an
   * empty array plus a falsy `includeUnassigned` means "no assignee filter".
   */
  assignedEmployeeIds: string[];
  includeUnassigned: boolean;
  searchTerm: string;
  sortBy: SmartOrderInboundHistoryOrderBy;
}

/**
 * The slice of the list filters that survives an app/tab close, stored per
 * employee so two users sharing a device never inherit each other's filters.
 */
export interface PersistedInboundFilters extends InboundListQueryFilters {
  activeStatusFilter: InboundListFilter;
}

// Keyed by orderUUIDs[0] - the only identifier order-summary.tsx receives
// (its smartOrderUUID route param) - so a second Continue click for the same
// inbound simply overwrites the pending entry with fresh timing.
export interface PendingInboundReviewTiming {
  continuePressedAt: number;
  inboundReviewEndSource: 'continue_click' | 'interaction';
  // End of the inbound-review phase only (start -> Continue), distinct from
  // the full review duration (start -> submission) computed downstream.
  inboundReviewEndTime: number;
  orderUUID: string;
  orderUUIDs: string[];
  restaurantUUID?: string;
  startSource: 'clicked_into' | 'interaction' | 'processed' | 'viewed';
  startTime: number;
  supplierUUID?: string;
}

export interface OrderInboundsState {
  actions: OrderInboundsActions;
  /**
   * Employee whose filters are currently live. `null` until the inbounds stack
   * mounts with an authenticated user; while null nothing is mirrored to
   * `filtersByEmployee`. Not persisted.
   */
  activeEmployeeUUID: string | null;
  activeStatusFilter: InboundListFilter;
  /** Last-applied filters keyed by employee UUID. The only persisted field. */
  filtersByEmployee: Record<string, PersistedInboundFilters>;
  inboundListCounts: Record<InboundListFilter, number>;
  inTrainingMode: boolean;
  isCorrectionInProgress: boolean;
  isInOrderInboundsStack: boolean;
  isSwapInProgress: boolean;
  pendingReviewTimingByOrderUUID: Record<string, PendingInboundReviewTiming>;
  queryFilters: InboundListQueryFilters;
  removedOrderUUIDs: Set<string>;
  savedSplitRatio: number;
  submittedSmartOrders: Record<string, boolean>;
}

interface OrderInboundsActions {
  addRemovedOrderUUID: (removedOrderUUID: string) => void;
  addSmartOrdersToSubmitQueue: (smartOrderUUIDs: string[]) => void;
  clearPendingSmartOrders: () => void;
  isSmartOrderSubmitted: (smartOrderUUID: string) => boolean;
  /**
   * Switches the live filters to the given employee's saved set (or the
   * defaults if they have none) and starts mirroring edits under that key.
   */
  setActiveEmployeeUUID: (employeeUUID: string | null) => void;
  setActiveStatusFilter: (filter: InboundListFilter) => void;
  setInboundListCount: (filter: InboundListFilter, count: number) => void;
  setInTrainingMode: (newInTrainingMode: boolean) => void;
  setIsCorrectionInProgress: (newIsCorrectionInProgress: boolean) => void;
  setIsInOrderInboundsStack: (value: boolean) => void;
  setIsSwapInProgress: (newIsSwapInProgress: boolean) => void;
  setPendingReviewTiming: (
    orderUUID: string,
    timing: PendingInboundReviewTiming,
  ) => void;
  setQueryFilters: (filters: Partial<InboundListQueryFilters>) => void;
  setSavedSplitRatio: (newSavedSplitRatio: number) => void;
  submitQueueSmartOrders: () => void;
  // Reads and removes one order's pending review timing, so completing (or
  // abandoning) it can never touch an unrelated, still-in-progress one.
  takePendingReviewTiming: (
    orderUUID: string,
  ) => PendingInboundReviewTiming | undefined;
}

const DEFAULT_ACTIVE_STATUS_FILTER = InboundListFilter.Open;

const DEFAULT_QUERY_FILTERS: InboundListQueryFilters = {
  assignedEmployeeIds: [],
  includeUnassigned: false,
  searchTerm: '',
  sortBy: SmartOrderInboundHistoryOrderBy.NewestFirst,
};

const STATUS_FILTER_VALUES = new Set<string>(Object.values(InboundListFilter));
const SORT_BY_VALUES = new Set<string>(
  Object.values(SmartOrderInboundHistoryOrderBy),
);

/**
 * Coerces a persisted entry -- which may be missing, partial, or written by an
 * older build -- into a valid filter set. Anything unrecognised falls back to
 * the default so a stale blob can never produce an invalid query.
 */
export const normalizePersistedFilters = (
  persisted: Partial<PersistedInboundFilters> | undefined,
): PersistedInboundFilters => {
  const activeStatusFilter =
    persisted?.activeStatusFilter !== undefined &&
    STATUS_FILTER_VALUES.has(persisted.activeStatusFilter)
      ? persisted.activeStatusFilter
      : DEFAULT_ACTIVE_STATUS_FILTER;

  let sortBy =
    persisted?.sortBy !== undefined && SORT_BY_VALUES.has(persisted.sortBy)
      ? persisted.sortBy
      : DEFAULT_QUERY_FILTERS.sortBy;

  // "Closest to cutoff" is unusable on the Closed tab (see the list screen's
  // filter form onChange). Enforce the same rule when restoring.
  if (
    activeStatusFilter === InboundListFilter.Closed &&
    sortBy === SmartOrderInboundHistoryOrderBy.ClosestCutoff
  ) {
    sortBy = SmartOrderInboundHistoryOrderBy.NewestFirst;
  }

  return {
    activeStatusFilter,
    assignedEmployeeIds: Array.isArray(persisted?.assignedEmployeeIds)
      ? persisted.assignedEmployeeIds.filter(
          (id): id is string => typeof id === 'string',
        )
      : [],
    includeUnassigned: persisted?.includeUnassigned === true,
    searchTerm:
      typeof persisted?.searchTerm === 'string' ? persisted.searchTerm : '',
    sortBy,
  };
};

/**
 * Copies the live filters into the active employee's persisted entry. Called
 * from every action that changes a persisted filter; a no-op until
 * `setActiveEmployeeUUID` has run.
 */
const mirrorFiltersToPersisted = (state: OrderInboundsState) => {
  if (!state.activeEmployeeUUID) {
    return;
  }
  state.filtersByEmployee[state.activeEmployeeUUID] = {
    activeStatusFilter: state.activeStatusFilter,
    assignedEmployeeIds: [...state.queryFilters.assignedEmployeeIds],
    includeUnassigned: state.queryFilters.includeUnassigned,
    searchTerm: state.queryFilters.searchTerm,
    sortBy: state.queryFilters.sortBy,
  };
};

export const useOrderInboundsStore = create<OrderInboundsState>()(
  persist(
    immer((set, get) => ({
      actions: {
        addRemovedOrderUUID: (removedOrderUUID) =>
          set((state) => {
            state.removedOrderUUIDs.add(removedOrderUUID);
          }),
        addSmartOrdersToSubmitQueue: (smartOrderUUIDS) =>
          set((state) => {
            for (const uuid of smartOrderUUIDS) {
              state.submittedSmartOrders[uuid] = false;
            }
          }),
        clearPendingSmartOrders: () =>
          set((state) => {
            // remove any smart orders where the UUID maps to `false`, because
            // they have not been submitted
            for (const [uuid, isPending] of Object.entries(
              state.submittedSmartOrders,
            )) {
              if (!isPending) {
                delete state.submittedSmartOrders[uuid];
              }
            }
          }),
        isSmartOrderSubmitted: (smartOrderUUID) =>
          get().submittedSmartOrders[smartOrderUUID],
        setActiveEmployeeUUID: (employeeUUID) =>
          set((state) => {
            state.activeEmployeeUUID = employeeUUID;
            const { activeStatusFilter, ...queryFilters } =
              normalizePersistedFilters(
                employeeUUID
                  ? state.filtersByEmployee[employeeUUID]
                  : undefined,
              );
            state.activeStatusFilter = activeStatusFilter;
            state.queryFilters = queryFilters;
            // Write the normalised set back so a repaired entry stays repaired.
            mirrorFiltersToPersisted(state);
          }),
        setActiveStatusFilter: (filter) =>
          set((state) => {
            state.activeStatusFilter = filter;
            mirrorFiltersToPersisted(state);
          }),
        setInboundListCount: (filter, count) =>
          set((state) => {
            state.inboundListCounts[filter] = count;
          }),
        setInTrainingMode: (newInTrainingMode) =>
          set((state) => {
            state.inTrainingMode = newInTrainingMode;
          }),
        setIsCorrectionInProgress: (newIsCorrectionInProgress) =>
          set((state) => {
            state.isCorrectionInProgress = newIsCorrectionInProgress;
          }),
        setIsInOrderInboundsStack: (value) =>
          set((state) => {
            state.isInOrderInboundsStack = value;
            if (!value) {
              state.queryFilters.searchTerm = '';
              mirrorFiltersToPersisted(state);
            }
          }),
        setIsSwapInProgress: (newIsSwapInProgress) =>
          set((state) => {
            state.isSwapInProgress = newIsSwapInProgress;
          }),
        setPendingReviewTiming: (orderUUID, timing) =>
          set((state) => {
            state.pendingReviewTimingByOrderUUID[orderUUID] = timing;
          }),
        setQueryFilters: (filters) =>
          set((state) => {
            Object.assign(state.queryFilters, filters);
            mirrorFiltersToPersisted(state);
          }),
        setSavedSplitRatio: (newSavedSplitRatio) =>
          set((state) => {
            state.savedSplitRatio = newSavedSplitRatio;
          }),
        submitQueueSmartOrders: () =>
          set((state) => {
            // Only orders that were still pending (not yet network-confirmed)
            // count as newly submitted here -- an order already marked `true`
            // was confirmed by an earlier call and shouldn't be double-counted.
            let newlyConfirmedCount = 0;
            for (const uuid of Object.keys(state.submittedSmartOrders)) {
              if (!state.submittedSmartOrders[uuid]) {
                newlyConfirmedCount += 1;
              }
              state.submittedSmartOrders[uuid] = true;
            }
            if (newlyConfirmedCount > 0) {
              // Optimistic nudge for instant badge feedback, ahead of the
              // subscription round trip that will actually move these orders'
              // cached rows from Open to Closed. Safe to mutate directly
              // here (bypassing a dedicated action) because total_count is
              // kept live-accurate in the Apollo cache on every write (see
              // mergeListShapeIntoHistoryQuery in queries.ts) -- the next
              // cache write for Open/Closed will unconditionally overwrite
              // these counts via setInboundListCount's absolute set, so this
              // guess can never compound into permanent drift.
              state.inboundListCounts[InboundListFilter.Open] = Math.max(
                0,
                state.inboundListCounts[InboundListFilter.Open] -
                  newlyConfirmedCount,
              );
              state.inboundListCounts[InboundListFilter.Closed] +=
                newlyConfirmedCount;
            }
          }),
        takePendingReviewTiming: (orderUUID) => {
          const timing = get().pendingReviewTimingByOrderUUID[orderUUID];
          if (timing) {
            set((state) => {
              delete state.pendingReviewTimingByOrderUUID[orderUUID];
            });
          }
          return timing;
        },
      },
      activeEmployeeUUID: null,
      activeStatusFilter: DEFAULT_ACTIVE_STATUS_FILTER,
      filtersByEmployee: {},
      inboundListCounts: {
        [InboundListFilter.All]: 0,
        [InboundListFilter.Open]: 0,
        [InboundListFilter.Closed]: 0,
      },
      inTrainingMode: false,
      isCorrectionInProgress: false,
      isInOrderInboundsStack: false,
      isSwapInProgress: false,
      pendingReviewTimingByOrderUUID: {},
      queryFilters: { ...DEFAULT_QUERY_FILTERS },
      removedOrderUUIDs: new Set(),
      savedSplitRatio: DEFAULT_SPLIT_RATIO,
      submittedSmartOrders: {},
    })),
    {
      // Only the per-employee filter map is persisted. Everything else
      // (counts, in-progress flags, the submit queue, the Set of removed
      // UUIDs) is session state and must start fresh on every launch.
      merge: (persistedState, currentState) => {
        const persisted = persistedState as
          | Partial<Pick<OrderInboundsState, 'filtersByEmployee'>>
          | undefined;
        const filtersByEmployee = persisted?.filtersByEmployee;
        return {
          ...currentState,
          filtersByEmployee:
            filtersByEmployee !== null &&
            typeof filtersByEmployee === 'object' &&
            !Array.isArray(filtersByEmployee)
              ? filtersByEmployee
              : {},
        };
      },
      name: ORDER_INBOUNDS_STORAGE_KEYS.LIST_FILTERS,
      partialize: (state) => ({ filtersByEmployee: state.filtersByEmployee }),
      storage: createJSONStorage(() => ({
        getItem: (key) => orderInboundsStorage.getString(key) ?? null,
        removeItem: (key) => orderInboundsStorage.remove(key),
        setItem: (key, value) => orderInboundsStorage.set(key, value),
      })),
      version: 1,
    },
  ),
);
